import classNames from "classnames";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  getPriceByCruise,
  selectSearch,
  selectSearchResultsByFilter,
} from "../../../store/selectors";
import { SearchAPI } from "../../../store/services/SearchService";
import {
  sortDate,
  sortName,
  sortPrice,
} from "../../../store/slices/searchFilterSlice";
import { ICruise } from "../../../store/slices/searchSlice";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import { countryCodeToFullName } from "../../../utils/helpers/country";
import { currencyToFormat } from "../../../utils/helpers/currency";
import SearchFilter from "../../base/SearchFilter";
import LoadingContainer from "../../containers/LoadingContainer";
import CustomLink from "../../shared/CustomLink";
import PaginationComponent from "../../shared/Pagination";

import styles from "./index.module.scss";

function SearchResults() {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();

  const { pathname, search } = useLocation();

  const { results, isLoading } = useTypedSelector(selectSearchResultsByFilter);
  const { results: searchResults } = useTypedSelector(selectSearch);

  const {
    search_item_image_source,
    date_format,
    api_language: language,
    default_search_sort,
  } = useTypedSelector((state) => state.environment);

  const [items, setItems] = useState<ICruise[]>([]);

  const appliedEnvSortRef = useRef(false);

  const initSearchResults = () => {
    dispatch(
      SearchAPI.endpoints.initSearchResults.initiate({ search, language }),
    );
  };

  const getPriceFrom = (cruise: ICruise) => {
    const price = getPriceByCruise(cruise);

    return currencyToFormat(price, cruise?.markets?.currency ?? "USD");
  };

  const getItemImageSource = useCallback(
    (cruise: ICruise): string | undefined => {
      const byKey = () => {
        switch (search_item_image_source) {
          case "ship":
            return cruise?.ship?.images?.[0];

          case "cruise":
            return cruise?.cruise?.images?.[0];

          case "map":
            return cruise?.cruise?.map;

          default:
            return undefined;
        }
      };

      return (
        byKey() ||
        cruise?.ship?.images?.[0] ||
        cruise?.cruise?.images?.[0] ||
        cruise?.cruise?.map
      );
    },
    [search_item_image_source],
  );

  useEffect(() => {
    initSearchResults();

    if (!appliedEnvSortRef.current && default_search_sort) {
      const key = String(default_search_sort).toLowerCase();

      switch (key) {
        case "date_asc": {
          dispatch(sortDate(true));
          break;
        }

        case "date_desc": {
          dispatch(sortDate(false));
          break;
        }

        case "price_asc": {
          dispatch(sortPrice(true));
          break;
        }

        case "price_desc": {
          dispatch(sortPrice(false));
          break;
        }

        case "name_asc": {
          dispatch(sortName(true));
          break;
        }

        case "name_desc": {
          dispatch(sortName(false));
          break;
        }

        default: {
          break;
        }
      }

      appliedEnvSortRef.current = true;
    }
  }, []);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t("search results")}</h1>

      <LoadingContainer isLoading={isLoading}>
        {searchResults && searchResults?.length > 1 && <SearchFilter />}

        <div className={styles.results}>
          {!items?.length ? (
            <p>{t("no results found")}</p>
          ) : (
            items.map((cruise) => {
              const priceFrom = getPriceFrom(cruise);
              const imageSource = getItemImageSource(cruise);

              return (
                <div key={cruise.code} className={styles.item}>
                  {imageSource && (
                    <img
                      alt={cruise?.cruise?.name}
                      src={imageSource}
                      className={classNames(styles.image, {
                        [styles.image_map]: search_item_image_source === "map",
                      })}
                    />
                  )}

                  <div className={styles.bottom}>
                    <div className={styles.body}>
                      <p className={styles.name}>{cruise?.cruise?.name}</p>

                      <table className={styles.details}>
                        <tbody>
                          <tr>
                            <td>{t("SHIP NAME")}</td>
                            <td>{cruise?.ship?.name}</td>
                          </tr>

                          <tr>
                            <td>{t("REGION")}</td>
                            <td>
                              {cruise?.cruise?.countries
                                ?.map(countryCodeToFullName)
                                .join(", ")}
                            </td>
                          </tr>

                          <tr>
                            <td>{t("DEPARTS")}</td>
                            <td>{dayjs(cruise?.embark).format(date_format)}</td>
                          </tr>

                          <tr>
                            <td>{t("NIGHTS")}</td>
                            <td>{cruise?.cruise.nights}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className={styles.footer}>
                      <div className={styles.price}>
                        <span className={styles.price_from}>{t("From")}</span>

                        <span className={styles.price_value}>
                          {priceFrom}&nbsp;pp
                        </span>
                      </div>

                      <CustomLink
                        className={styles.button}
                        to={`${pathname}/${cruise.code}${search}&source=${cruise.source}`}>
                        {t("next")}
                      </CustomLink>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <PaginationComponent data={results} setData={setItems} />
      </LoadingContainer>
    </div>
  );
}

export default SearchResults;
